import { useCallback, useRef, useState, useEffect } from 'react';

export const usePullToRefresh = (onRefresh, options = {}) => {
  const {
    pullDownThreshold = 80,
    maxPullDownDistance = 120,
    resistance = 2.5,
    refreshThreshold = 60,
  } = options;

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (isRefreshing) return;

    const touch = e.touches[0];
    const container = containerRef.current;

    // Check if we're at the top of any scrollable area
    const isAtTop = () => {
      // Check document body/window scroll first (most common case)
      const documentScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (documentScrollTop > 0) return false;

      // Check the container itself
      if (container && container.scrollTop > 0) return false;

      // Check if there's any scrollable parent that has content above
      let element = container;
      while (element && element !== document.body) {
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // If this element is scrollable and not at the top, don't allow pull
        if (scrollHeight > clientHeight && scrollTop > 0) {
          return false;
        }

        element = element.parentElement;
      }

      return true;
    };

    // Only allow pull-to-refresh when truly at the top with no scrollable content above
    if (isAtTop()) {
      setStartY(touch.clientY);
      setIsPulling(true);
    }
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;

    const touch = e.touches[0];
    const currentY = touch.clientY;
    const deltaY = currentY - startY;

    if (deltaY > 0) { // Only allow downward pull
      e.preventDefault(); // Prevent native scrolling during pull (will work with passive: false)
      const distance = Math.min(deltaY / resistance, maxPullDownDistance);
      setPullDistance(distance);
    }
  }, [isPulling, startY, resistance, maxPullDownDistance, isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || isRefreshing) return;

    setIsPulling(false);

    if (pullDistance >= refreshThreshold) {
      setIsRefreshing(true);

      try {
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        console.error('Pull-to-refresh failed:', error);
      } finally {
        setIsRefreshing(false);
      }
    }

    setPullDistance(0);
  }, [isPulling, pullDistance, refreshThreshold, onRefresh, isRefreshing]);

  const resetPull = useCallback(() => {
    setPullDistance(0);
    setIsPulling(false);
    setIsRefreshing(false);
  }, []);

  // Set up manual event listeners with passive: false for touchmove
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const options = { passive: false };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, options);
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Auto-reset if component unmounts while refreshing
  useEffect(() => {
    return () => {
      if (isRefreshing) {
        setIsRefreshing(false);
      }
    };
  }, [isRefreshing]);

  return {
    containerRef,
    pullDistance,
    isRefreshing,
    isPulling,
    resetPull,
  };
};
